import { useState } from 'react';
import type { ScoreMetricResult, ScorePriorityItem, ScoringPriorityLevel } from '@/types/gmb';
import { formatPriorityTitle, metricStatusText } from '@/pages/gmb-audit/utils/reportDisplay';
import { findDimensionForMetric } from '@/pages/gmb-audit/utils/diagnosisDimensions';

// UI-2B｜現在先做這 3 件事（純顯示層）。
// 只讀 scoreResult.priorities（Backend 已排序 Top 3）與既有 metrics；
// 不重新排序、不重新選 Top 3、不改 priority algorithm。

const RANK_MARK = ['①', '②', '③'];

/** 僅 HIGH / MEDIUM 顯示等級標籤；LOW / NONE 不當成重大問題。 */
const LEVEL_LABEL: Partial<Record<ScoringPriorityLevel, string>> = {
  HIGH: '高優先',
  MEDIUM: '中優先',
};

const LEVEL_STYLE: Record<ScoringPriorityLevel, string> = {
  HIGH: 'bg-accent-100 text-accent-900 ring-1 ring-accent-200',
  MEDIUM: 'bg-secondary-100 text-secondary-900 ring-1 ring-secondary-200',
  LOW: 'bg-background-200 text-foreground-600 ring-1 ring-background-300',
  NONE: 'bg-background-200 text-foreground-600 ring-1 ring-background-300',
};

function ActionCard({
  item,
  index,
  metric,
}: {
  item: ScorePriorityItem;
  index: number;
  metric?: ScoreMetricResult;
}) {
  const [open, setOpen] = useState(false);
  const dimension = findDimensionForMetric(item.metricKey);
  const showLevel = item.level === 'HIGH' || item.level === 'MEDIUM';
  const title = formatPriorityTitle(item.metricKey, item.title);

  const currentText = metric
    ? metric.status === 'SCORED' && typeof metric.score === 'number'
      ? `目前 ${metric.score} / ${metric.maxScore} 分`
      : metricStatusText(metric)
    : '待改善';

  return (
    <div className="flex flex-col rounded-2xl border border-background-300 bg-background-50 p-5 shadow-card md:p-6">
      <div className="flex items-center justify-between gap-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-700 text-base font-bold text-background-50">
          {RANK_MARK[index] ?? item.rank}
        </span>
        {showLevel && (
          <span className={`ds-badge ${LEVEL_STYLE[item.level]}`}>
            <i className="ri-arrow-up-line" />
            {LEVEL_LABEL[item.level]}
          </span>
        )}
      </div>

      <h3 className="mt-3.5 text-lg leading-snug text-foreground-950">{title}</h3>

      <div className="mt-3 flex items-baseline gap-2">
        <span className="text-3xl font-bold leading-none text-foreground-950">{item.deficit}</span>
        <span className="text-sm font-medium text-foreground-500">此項最多可補回分數</span>
      </div>

      <dl className="mt-4 space-y-2.5 border-t border-background-200 pt-4 text-sm">
        <div className="flex gap-2">
          <dt className="w-16 shrink-0 text-foreground-500">目前狀況</dt>
          <dd className="font-semibold text-foreground-900">{currentText}</dd>
        </div>
        {dimension && (
          <div className="flex gap-2">
            <dt className="w-16 shrink-0 text-foreground-500">為什麼重要</dt>
            <dd className="text-foreground-700">{dimension.caption}</dd>
          </div>
        )}
        <div className="flex gap-2">
          <dt className="w-16 shrink-0 text-foreground-500">下一步</dt>
          <dd className="text-foreground-700">{item.recommendation}</dd>
        </div>
      </dl>

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="mt-4 flex items-center gap-1.5 self-start text-sm font-semibold text-primary-700"
      >
        {open ? '收合原因' : '查看原因'}
        <i className={`${open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-lg`} />
      </button>

      {open && (
        <div className="mt-3 rounded-xl border border-background-200 bg-background-100 p-4">
          <p className="text-sm leading-relaxed text-foreground-700">
            {metric ? metric.reason : item.recommendation}
          </p>
          {metric && (
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.recommendation}</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function ReportPrioritySection({
  priorities,
  metrics,
}: {
  priorities: ScorePriorityItem[];
  metrics: ScoreMetricResult[];
}) {
  const top = (priorities || []).slice(0, 3);
  const metricMap = new Map<string, ScoreMetricResult>();
  (metrics || []).forEach((m) => metricMap.set(m.key, m));

  return (
    <section className="ds-container pt-12 md:pt-16">
      <div className="mb-3 flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-50 text-primary-700">
          <i className="ri-focus-3-line text-xl" />
        </span>
        <h2 className="text-xl md:text-2xl">現在先做這 3 件事</h2>
      </div>
      <p className="mb-6 text-base text-foreground-500">
        依目前健診結果排序，先處理這幾項對健康度影響最大的缺口。
      </p>

      {top.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-background-400 bg-background-100 p-8 text-center text-base text-foreground-600">
          目前沒有明顯需要優先改善的項目。
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {top.map((item, index) => (
            <ActionCard
              key={item.metricKey}
              item={item}
              index={index}
              metric={metricMap.get(item.metricKey)}
            />
          ))}
        </div>
      )}
    </section>
  );
}